import { defineStore } from 'pinia'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import store from '@/store/store'; 
dayjs.extend(relativeTime)

const ACTIVITY_TYPES = {
  // Invoices
  INVOICE_SENT: {
    icon: 'mdi-file-document',
    color: 'primary',
    getTitle: (id) => `Invoice #${id} sent`,
  },
  INVOICE_DELETED: {
    icon: 'mdi-trash-can',
    color: 'red',
    getTitle: (id) => `Invoice #${id} deleted`,
  },
  INVOICE_PAID: {
    icon: 'mdi-credit-card-check',
    color: 'green',
    getTitle: (id) => `Invoice #${id} paid`,
  },
  INVOICE_OVERDUE: {
    icon: 'mdi-calendar-alert',
    color: 'orange',
    getTitle: (id) => `Invoice #${id} overdue`,
  },

  // Payments
  PAYMENT_RECEIVED: {
    icon: 'mdi-credit-card-check',
    color: 'green',
    getTitle: (name) => `Payment received from ${name}`,
  },
  PAYMENT_FAILED: {
    icon: 'mdi-alert-circle',
    color: 'red',
    getTitle: (name) => `Payment failed from ${name}`,
  },

  // Reminders
  REMINDER_SENT: {
    icon: 'mdi-email',
    color: 'orange',
    getTitle: (name) => `Reminder sent to ${name}`,
  },

  // Email Actions
  EMAIL_SCHEDULED: {
    icon: 'mdi-calendar-clock',
    color: 'blue',
    getTitle: (count) => `${count} email(s) scheduled`,
  },
  EMAIL_FAILED: {
    icon: 'mdi-email-remove',
    color: 'red',
    getTitle: (reason) => `Email failed: ${reason}`,
  },

  // Clients
  CLIENT_CREATED: {
    icon: 'mdi-account-plus',
    color: 'indigo',
    getTitle: (name) => `Client ${name} created`,
  },
  CLIENT_UPDATED: {
    icon: 'mdi-account-edit',
    color: 'deep-purple',
    getTitle: (name) => `Client ${name} updated`,
  },
  CLIENT_DELETED: {
    icon: 'mdi-account-remove',
    color: 'error',
    getTitle: () => `Client deleted`,
  },

  // Company/Account
  COMPANY_CREATED: {
    icon: 'mdi-office-building',
    color: 'indigo',
    getTitle: (name) => `Company ${name} created`,
  },
  COMPANY_UPDATED: {
    icon: 'mdi-office-building',
    color: 'deep-purple',
    getTitle: (name) => `Company ${name} updated`,
  },
  COMPANY_DELETED: {
    icon: 'mdi-office-building',
    color: 'error',
    getTitle: () => `Company deleted`,
  },

  // Authentication
  LOGIN_SUCCESS: {
    icon: 'mdi-login',
    color: 'teal',
    getTitle: () => `Login successful`,
  },
  LOGIN_FAILED: {
    icon: 'mdi-shield-alert',
    color: 'red',
    getTitle: () => `Login attempt failed`,
  },
  LOGOUT: {
    icon: 'mdi-logout',
    color: 'grey',
    getTitle: () => `User logged out`,
  },

  // Miscellaneous
  SYSTEM_ERROR: {
    icon: 'mdi-alert-circle',
    color: 'red',
    getTitle: (message) => `System error: ${message}`,
  },
  DATA_SYNC: {
    icon: 'mdi-cloud-upload',
    color: 'blue',
    getTitle: (status) => `Data sync ${status}`,
  },
  FEATURE_ENABLED: {
    icon: 'mdi-toggle-switch',
    color: 'green',
    getTitle: (feature) => `${feature} enabled`,
  },
  FEATURE_DISABLED: {
    icon: 'mdi-toggle-switch-off',
    color: 'grey',
    getTitle: (feature) => `${feature} disabled`,
  },
  USER_PROFILE_UPDATED: {
    icon: 'mdi-account-edit',
    color: 'purple',
    getTitle: () => `User profile updated`,
  },
}



export const useActivityStore = defineStore('activity', {
  state: () => ({
    activities: [],
    userId: store.state.user?.user_id || null,
  }),

  actions: {
    setUserId(userId) {
      if (this.userId !== userId) {
        this.userId = userId;
        this.loadFromLocalStorage();
      }
    },

    addActivity(type, payload) {
      if (!this.userId) return;

      const typeInfo = ACTIVITY_TYPES[type];
      if (!typeInfo) return;

      const newActivity = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        icon: typeInfo.icon,
        title: typeInfo.getTitle(payload),
        color: typeInfo.color,
        time: 'just now',
      };

      this.activities.unshift(newActivity);

      if (this.activities.length > 10) {
        this.activities = this.activities.slice(0, 10);
      }

      this.persistToLocalStorage();
    },

    removeActivity(id) {
      this.activities = this.activities.filter(a => a.id !== id);
      this.persistToLocalStorage();
    },

    persistToLocalStorage() {
      if (this.userId) {
        localStorage.setItem(`user_activities_${this.userId}`, JSON.stringify(this.activities));
      }
    },

    loadFromLocalStorage() {
      if (!this.userId) return;

      const saved = localStorage.getItem(`user_activities_${this.userId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.activities = parsed.map(a => ({
          ...a,
          time: dayjs(a.timestamp).fromNow(),
        }));
      } else {
        this.activities = [];
      }
    },

    clearActivities() {
      if (this.userId) {
        localStorage.removeItem(`user_activities_${this.userId}`);
      }
      this.activities = [];
    },

    refreshTimeLabels() {
      this.activities = this.activities.map(a => ({
        ...a,
        time: dayjs(a.timestamp).fromNow(),
      }));
    },
  },
});